import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropzone",
    "fileInput",
    "fileList",
    "uploadButton",
    "progressBar",
    "statusMessage"
  ]

  static values = {
    maxFileSize: Number,
    acceptedTypes: String,
    uploadUrl: String,
    multiple: Boolean
  }

  connect() {
    this.setupDropzone()
    this.files = []
    this.maxFileSizeValue = this.maxFileSizeValue || 50 * 1024 * 1024 // 50MB default
  }

  setupDropzone() {
    if (!this.hasDropzoneTarget) return

    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.preventDefaults.bind(this), false)
      document.body.addEventListener(eventName, this.preventDefaults.bind(this), false)
    })

    // Highlight drop zone when item is dragged over it
    ['dragenter', 'dragover'].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.highlight.bind(this), false)
    })

    ['dragleave', 'drop'].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.unhighlight.bind(this), false)
    })

    // Handle dropped files
    this.dropzoneTarget.addEventListener('drop', this.handleDrop.bind(this), false)
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight(e) {
    this.dropzoneTarget.classList.add('border-primary', 'bg-base-200', 'scale-105')
    this.dropzoneTarget.classList.remove('border-base-300')
  }

  unhighlight(e) {
    this.dropzoneTarget.classList.remove('border-primary', 'bg-base-200', 'scale-105')
    this.dropzoneTarget.classList.add('border-base-300')
  }

  handleDrop(e) {
    const dt = e.dataTransfer
    const files = dt.files

    this.handleFiles(files)
  }

  triggerFileInput() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const files = event.target.files
    this.handleFiles(files)
  }

  handleFiles(files) {
    const filesArray = [...files]
    
    // Filter files based on accepted types and size
    const validFiles = filesArray.filter(file => this.validateFile(file))
    
    if (this.multipleValue) {
      this.files = [...this.files, ...validFiles]
    } else {
      this.files = validFiles.slice(0, 1)
    }

    this.updateFileList()
    this.updateUploadButton()
  }

  validateFile(file) {
    // Check file size
    if (file.size > this.maxFileSizeValue) {
      this.showError(`File "${file.name}" exceeds maximum size of ${this.formatFileSize(this.maxFileSizeValue)}`)
      return false
    }

    // Check file type if specified
    if (this.acceptedTypesValue) {
      const acceptedTypes = this.acceptedTypesValue.split(',').map(t => t.trim())
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
      const mimeType = file.type

      const isAccepted = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type.toLowerCase()
        } else if (type.includes('*')) {
          const [mainType] = type.split('/')
          return mimeType.startsWith(mainType)
        } else {
          return mimeType === type
        }
      })

      if (!isAccepted) {
        this.showError(`File type "${file.type || fileExtension}" is not accepted`)
        return false
      }
    }

    return true
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.fileIndex)
    this.files.splice(index, 1)
    this.updateFileList()
    this.updateUploadButton()
  }

  updateFileList() {
    if (!this.hasFileListTarget) return

    if (this.files.length === 0) {
      this.fileListTarget.innerHTML = `
        <div class="text-center py-4 text-base-content/60">
          No files selected
        </div>
      `
      return
    }

    const fileItems = this.files.map((file, index) => `
      <div class="flex items-center justify-between p-3 bg-base-100 rounded-lg border border-base-300">
        <div class="flex items-center space-x-3">
          <div class="flex-shrink-0">
            ${this.getFileIcon(file)}
          </div>
          <div>
            <div class="font-medium text-sm">${file.name}</div>
            <div class="text-xs text-base-content/60">${this.formatFileSize(file.size)}</div>
          </div>
        </div>
        <button 
          type="button"
          class="btn btn-ghost btn-sm btn-circle"
          data-action="click->drag-drop-upload#removeFile"
          data-file-index="${index}"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `).join('')

    this.fileListTarget.innerHTML = fileItems
  }

  getFileIcon(file) {
    const type = file.type.split('/')[0]
    
    if (type === 'image') {
      // Create thumbnail for images
      return `<div class="w-10 h-10 bg-base-200 rounded overflow-hidden">
        <img src="${URL.createObjectURL(file)}" class="w-full h-full object-cover" />
      </div>`
    }
    
    // Icon based on file type
    const icons = {
      'application/pdf': `<svg class="w-10 h-10 text-error" fill="currentColor" viewBox="0 0 20 20"><path d="M4 18h12V6h-4V2H4v16zm8-12h3.586L12 2.414V6zm-8 0V2a2 2 0 012-2h7.172a2 2 0 011.414.586l3.828 3.828A2 2 0 0118 5.828V18a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z"/></svg>`,
      'text': `<svg class="w-10 h-10 text-info" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fill-rule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 100 4h2a2 2 0 100-4 1 1 0 100-2zm9 2a1 1 0 10-2 0v6a1 1 0 102 0V7zm-3 8a1 1 0 100-2H6a1 1 0 100 2h4z" clip-rule="evenodd"/></svg>`,
      'audio': `<svg class="w-10 h-10 text-warning" fill="currentColor" viewBox="0 0 20 20"><path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"/></svg>`,
      'default': `<svg class="w-10 h-10 text-base-content/40" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fill-rule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 100 4h2a2 2 0 100-4 1 1 0 100-2zm9 2a1 1 0 10-2 0v6a1 1 0 102 0V7zm-3 8a1 1 0 100-2H6a1 1 0 100 2h4z" clip-rule="evenodd"/></svg>`
    }

    const fileType = file.type.split('/')[0]
    return icons[file.type] || icons[fileType] || icons.default
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  updateUploadButton() {
    if (!this.hasUploadButtonTarget) return

    if (this.files.length > 0) {
      this.uploadButtonTarget.disabled = false
      this.uploadButtonTarget.classList.remove('btn-disabled')
      this.uploadButtonTarget.textContent = `Upload ${this.files.length} file${this.files.length > 1 ? 's' : ''}`
    } else {
      this.uploadButtonTarget.disabled = true
      this.uploadButtonTarget.classList.add('btn-disabled')
      this.uploadButtonTarget.textContent = 'Upload Files'
    }
  }

  async uploadFiles() {
    if (this.files.length === 0) return

    this.showProgress(0)
    this.disableControls()

    try {
      const totalFiles = this.files.length
      let uploadedCount = 0

      for (const file of this.files) {
        await this.uploadFile(file)
        uploadedCount++
        const progress = (uploadedCount / totalFiles) * 100
        this.showProgress(progress)
      }

      this.showSuccess(`Successfully uploaded ${uploadedCount} file${uploadedCount > 1 ? 's' : ''}`)
      this.clearFiles()
      
      // Trigger a Turbo refresh or custom event
      this.dispatch("upload-complete", { detail: { filesUploaded: uploadedCount } })
      
    } catch (error) {
      this.showError(`Upload failed: ${error.message}`)
    } finally {
      this.enableControls()
      this.hideProgress()
    }
  }

  async uploadFile(file) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('authenticity_token', this.getCSRFToken())

    const response = await fetch(this.uploadUrlValue, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': this.getCSRFToken()
      }
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || `Failed to upload ${file.name}`)
    }

    return response.json()
  }

  clearFiles() {
    this.files = []
    this.updateFileList()
    this.updateUploadButton()
    
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ''
    }
  }

  showProgress(percentage) {
    if (!this.hasProgressBarTarget) return

    this.progressBarTarget.style.display = 'block'
    const progressFill = this.progressBarTarget.querySelector('.progress-fill') || this.progressBarTarget
    progressFill.style.width = `${percentage}%`
    progressFill.setAttribute('aria-valuenow', percentage)
  }

  hideProgress() {
    if (!this.hasProgressBarTarget) return
    
    setTimeout(() => {
      this.progressBarTarget.style.display = 'none'
    }, 1000)
  }

  showSuccess(message) {
    this.showMessage(message, 'success')
  }

  showError(message) {
    this.showMessage(message, 'error')
  }

  showMessage(message, type) {
    if (!this.hasStatusMessageTarget) return

    this.statusMessageTarget.textContent = message
    this.statusMessageTarget.className = `alert alert-${type}`
    this.statusMessageTarget.style.display = 'block'

    if (type === 'success') {
      setTimeout(() => {
        this.statusMessageTarget.style.display = 'none'
      }, 5000)
    }
  }

  disableControls() {
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.disabled = true
      this.uploadButtonTarget.classList.add('loading')
    }
    
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('pointer-events-none', 'opacity-50')
    }
  }

  enableControls() {
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.disabled = false
      this.uploadButtonTarget.classList.remove('loading')
    }
    
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('pointer-events-none', 'opacity-50')
    }
  }

  getCSRFToken() {
    return document.querySelector('[name="csrf-token"]')?.content || ''
  }

  // Paste handling for images
  handlePaste(event) {
    const items = event.clipboardData?.items
    if (!items) return

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile()
        if (blob) {
          const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type })
          this.handleFiles([file])
        }
      }
    }
  }
}